import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { NAV_LINKS, SITE } from "@/data/content";

const navTestId = (label) => `nav-link-${label.toLowerCase().replace(/\s+/g, "-")}`;

const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      data-testid="site-header"
      className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-ink/95 backdrop-blur-xl"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-2.5 lg:px-12 lg:py-3">
        <Link to="/" data-testid="brand-logo" className="flex items-center" aria-label="Evaya Ventures home">
          <img
            src={SITE.logo}
            alt="Evaya Ventures Pvt. Ltd. — from Earth to Extraordinary"
            className="h-auto w-[150px] lg:w-[190px]"
          />
        </Link>

        <nav className="hidden items-center gap-7 xl:flex" aria-label="Primary">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              data-testid={navTestId(link.label)}
              className={({ isActive }) =>
                `text-[13px] font-medium tracking-wide transition-colors duration-300 ${
                  isActive ? "text-brand-light" : "text-white/70 hover:text-white"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <Link
            to="/contact"
            data-testid="header-cta-button"
            className="hidden items-center gap-2 rounded-sm bg-brand px-6 py-3 text-[13px] font-semibold tracking-wide text-ink transition-colors duration-300 hover:bg-brand-light md:inline-flex"
          >
            Talk to Us
            <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
          </Link>
          <button
            type="button"
            data-testid="mobile-menu-button"
            aria-label={open ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="flex h-11 w-11 items-center justify-center rounded-sm border border-white/20 text-white transition-colors duration-300 hover:border-brand xl:hidden"
          >
            {open ? <X className="h-5 w-5" strokeWidth={1.5} /> : <Menu className="h-5 w-5" strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-nav" className="border-t border-white/10 bg-ink xl:hidden">
          <nav className="mx-auto flex max-w-7xl flex-col px-6 py-6" aria-label="Mobile">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.path}
                to={link.path}
                data-testid={`mobile-${navTestId(link.label)}`}
                className={({ isActive }) =>
                  `border-b border-white/5 py-4 font-display text-2xl transition-colors duration-300 ${
                    isActive ? "text-brand-light" : "text-white"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-header-cta-button"
              className="mt-6 inline-flex items-center justify-center gap-2 rounded-sm bg-brand px-6 py-4 text-sm font-semibold tracking-wide text-ink transition-colors duration-300 hover:bg-brand-light"
            >
              Talk to Us
              <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
